// Pillar Content Hub — 4 ultimate guides (Notary, NAATI, MFA Legalization, Visa)
// Each guide is a long-form authority resource: ~3,000–5,000 Thai words,
// 12–15 sections with TOC, HowTo steps, FAQs, and citations.

export type GuideSection = {
  id: string; // anchor slug (kebab-case ASCII)
  heading: string;
  paragraphs: string[];
  bullets?: string[];
  callout?: string;
};

export type GuideFAQ = { q: string; a: string };

export type GuideHowToStep = { name: string; text: string; duration?: string };

export type Guide = {
  slug: string;
  category: "Notary" | "NAATI" | "Legalization" | "Visa";
  title: string;          // SEO title
  h1: string;             // on-page H1
  tldr: string;           // 80–120 word executive summary (speakable)
  metaDesc: string;
  keywords: string[];
  readMinutes: number;
  updated: string;        // ISO date
  cover?: string;
  intro: string;          // 150–250 word lead paragraph
  sections: GuideSection[];
  howTo: { name: string; description: string; totalTime?: string; steps: GuideHowToStep[] };
  faqs: GuideFAQ[];
  relatedServiceSlugs: string[];
  citations: { name: string; url: string }[];
};

// ───────────────────────────────────────────────────────────────
// GUIDE 1 — NOTARY PUBLIC THAILAND ULTIMATE GUIDE
// ───────────────────────────────────────────────────────────────
const NOTARY: Guide = {
  slug: "notary-public-thailand",
  category: "Notary",
  title: "Notary Public ประเทศไทย 2025 — คู่มือฉบับสมบูรณ์ (Ultimate Guide) | NYC Legal",
  h1: "Notary Public ประเทศไทย — Ultimate Guide ฉบับปี 2025",
  tldr:
    "Notary Public ในประเทศไทยทำโดย “ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney)” ที่ขึ้นทะเบียนกับสภาทนายความในพระบรมราชูปถัมภ์ ไม่ใช่ Notary Public แบบประเทศตะวันตก — รับรองได้ทั้งลายมือชื่อ คำสาบาน สำเนาถูกต้อง คำแปล Affidavit Power of Attorney ใช้ส่งต่างประเทศต้องผ่าน MFA นิติกรณ์ + Embassy เพิ่ม ราคามาตรฐาน 700–1,500 บาทต่อตราประทับ ใช้เวลา 1 วันทำการ",
  metaDesc:
    "คู่มือ Notary Public ประเทศไทย 2025 ครบทุกประเด็น — กฎหมาย ราคา ขั้นตอน เอกสารที่รับรองได้ ตัวอย่าง Affidavit/POA ความต่างจาก US/UK/AU พร้อมแบบฟอร์มและ FAQ 20 ข้อ",
  keywords: [
    "notary public ไทย", "notary public thailand", "notarial services attorney",
    "ทนายโนตารี", "affidavit ไทย", "power of attorney thailand", "รับรองลายมือชื่อทนาย",
    "notary public ราคา", "ใบรับรองโสด affidavit", "consular legalization thailand",
  ],
  readMinutes: 22,
  updated: "2026-05-15",
  intro:
    "ในการทำธุรกรรมระหว่างประเทศ — ไม่ว่าจะเป็นการมอบอำนาจขายอสังหาริมทรัพย์ในต่างแดน การยื่นวีซ่าและ Green Card การขอสัญชาติ การเปิดบริษัทในต่างประเทศ การจดทะเบียนสมรสกับคู่รักต่างชาติ หรือการลงนามในสัญญาสำคัญที่ต้องบังคับใช้ในศาลต่างประเทศ — เอกสารที่ออกในประเทศไทยมักต้อง “รับรองโดย Notary Public” ก่อนเสมอ คู่มือฉบับนี้รวบรวมทุกสิ่งที่คุณต้องรู้เกี่ยวกับ Notary Public ในประเทศไทย โดยอ้างอิงข้อบังคับสภาทนายความ ว่าด้วยการขึ้นทะเบียนทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร พ.ศ. 2551 และระเบียบกระทรวงการต่างประเทศที่เกี่ยวข้อง พร้อมประสบการณ์ตรงจากทีมทนาย Notary 6 ท่านของ NYC ที่รับรองเอกสารปีละกว่า 12,000 ฉบับให้ลูกค้าใน 40+ ประเทศ",
  sections: [
    {
      id: "what-is-notary-thai",
      heading: "1. Notary Public ในประเทศไทยคืออะไร",
      paragraphs: [
        "ประเทศไทยไม่มี “Notary Public” ในความหมายเดียวกับสหรัฐอเมริกา สหราชอาณาจักร ออสเตรเลีย หรือประเทศ Civil Law อย่างฝรั่งเศส เยอรมนี อิตาลี ที่ Notary เป็นวิชาชีพอิสระแยกออกจากทนายความ และมีอำนาจตามกฎหมายโดยตรงในการรับรองธุรกรรม ทรัพย์มรดก หรือสัญญาสำคัญให้มีผลบังคับได้เทียบเท่าคำพิพากษา",
        "ในประเทศไทยใช้ระบบ “Notarial Services Attorney” คือทนายความที่ผ่านการอบรมและขึ้นทะเบียนพิเศษกับสภาทนายความในพระบรมราชูปถัมภ์ ตามข้อบังคับสภาทนายความ ว่าด้วยการขึ้นทะเบียนทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร พ.ศ. 2551 (และฉบับแก้ไขเพิ่มเติม) บุคคลเหล่านี้มีอำนาจรับรองลายมือชื่อ คำสาบาน สำเนาถูกต้อง คำแปล และทำเอกสารประเภท Affidavit, Statutory Declaration, Power of Attorney เพื่อใช้ในต่างประเทศ",
        "การรับรองโดย Notarial Services Attorney จึงเป็น “ขั้นแรก” ของห่วงโซ่การรับรองเอกสาร (chain of authentication) ก่อนส่งเอกสารไปกรมการกงสุล (MFA) เพื่อรับรองนิติกรณ์ และส่งต่อสถานทูตประเทศปลายทาง (Embassy Legalization) ตามลำดับ",
      ],
      callout:
        "Notary Public ในไทย = ทนายความที่ขึ้นทะเบียนเพิ่ม ไม่ใช่อาชีพแยก — ตรวจสอบทนายได้ที่ www.lawyerscouncil.or.th",
    },
    {
      id: "legal-framework",
      heading: "2. กรอบกฎหมายและข้อบังคับ",
      paragraphs: [
        "อำนาจการรับรองของ Notarial Services Attorney มาจากข้อบังคับสภาทนายความ ว่าด้วยการขึ้นทะเบียนทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร พ.ศ. 2551 ซึ่งกำหนดให้ทนายความที่จะขึ้นทะเบียนต้องมีใบอนุญาตว่าความและผ่านการอบรมหลักสูตรของสภาทนายความ ใช้เวลาอบรม 18 ชั่วโมงและสอบผ่าน",
        "เอกสารที่ได้รับการรับรองโดย Notarial Services Attorney จะถูกยอมรับโดยกรมการกงสุล กระทรวงการต่างประเทศของไทย ในการรับรองนิติกรณ์เพิ่มเติม ตามระเบียบกระทรวงการต่างประเทศ ว่าด้วยการรับรองเอกสาร พ.ศ. 2539 และที่แก้ไขเพิ่มเติม ซึ่งระบุประเภทเอกสารที่กรมการกงสุลรับรองได้ รวมถึงเอกสารแปล เอกสารรับรองโดย Notary และเอกสารราชการไทย",
        "ในระดับสากล Thailand ลงนามใน Apostille Convention (Hague Convention 5 ตุลาคม 1961) เมื่อ 30 มิถุนายน 2569 และมีผลบังคับใช้กับไทย 28 กุมภาพันธ์ 2570 ทำให้เอกสารที่รับรอง MFA Apostille ใช้ได้ใน 130 ประเทศสมาชิกโดยจะไม่ต้องผ่านสถานทูตอีก แต่ขั้นตอน Notary Public ในไทยยังคงเหมือนเดิม",
      ],
      bullets: [
        "ข้อบังคับสภาทนายความฯ ปี 2551 — ที่มาของอำนาจ Notary",
        "ระเบียบ MFA ปี 2539 — รับรองนิติกรณ์ของกรมการกงสุล",
        "Hague Apostille (มีผลกับไทย 28 ก.พ. 2570) — ลัดขั้นตอน Embassy",
      ],
    },
    {
      id: "what-can-be-notarized",
      heading: "3. เอกสารใดบ้างที่ Notary Public ในไทยรับรองได้",
      paragraphs: [
        "ขอบเขตการรับรองของ Notarial Services Attorney ครอบคลุมทั้งเอกสารส่วนบุคคล เอกสารบริษัท และคำแปล ซึ่งใช้ได้ทั้งในประเทศและส่งออกไปต่างประเทศ ในทางปฏิบัติเอกสารยอดนิยมที่ลูกค้า NYC ขอรับรองมากที่สุดประกอบด้วย:",
      ],
      bullets: [
        "Affidavit / Statutory Declaration — คำให้การสาบานตัว",
        "Affidavit of Single Status / Affidavit of Marital Status (เพื่อจดทะเบียนสมรสในต่างประเทศ)",
        "Power of Attorney (POA) — มอบอำนาจให้ผู้อื่นดำเนินการแทน",
        "Certified True Copy — รับรองสำเนาถูกต้อง",
        "Notarized Translation — รับรองว่าคำแปลตรงกับต้นฉบับ",
        "Acknowledgment of Signature — รับรองลายมือชื่อ",
        "Verification of Identity / Address Confirmation",
        "Board Resolution / Minutes of Meeting (บริษัท)",
        "Articles of Association (หนังสือบริคณห์สนธิ — ฉบับแปล)",
        "Certificate of Incumbency",
        "Trust Deed / Beneficiary Declaration",
        "Sponsor Letter / Invitation Letter (เพื่อยื่นวีซ่า)",
      ],
      callout:
        "เอกสารราชการไทย (ทะเบียนบ้าน บัตรประชาชน ทะเบียนสมรส) ไม่ต้องผ่าน Notary ก่อน — ส่งกรมการกงสุลรับรองนิติกรณ์ได้โดยตรง หลังแปลและรับรองคำแปลแล้ว",
    },
    {
      id: "notary-vs-mfa-vs-embassy",
      heading: "4. ความต่างระหว่าง Notary, MFA นิติกรณ์ และ Embassy Legalization",
      paragraphs: [
        "หนึ่งในความสับสนที่พบบ่อยที่สุดของลูกค้าคือ “ต้องผ่าน Notary หรือไม่ ผ่าน MFA แล้วทำไมต้องไปสถานทูตอีก” คำตอบคือทั้งสามขั้นตอนเป็นห่วงโซ่การรับรองที่ทำหน้าที่ต่างกัน",
        "Notary Public — รับรองว่า ‘ลายมือชื่อนี้เป็นของบุคคลนี้จริง’ หรือ ‘คำแปลนี้ตรงกับต้นฉบับ’ เป็นการรับรองตัวบุคคล/เนื้อหา ทำโดยทนายความที่ขึ้นทะเบียน",
        "MFA นิติกรณ์ — กรมการกงสุล กระทรวงการต่างประเทศ รับรองว่า ‘ตราประทับและลายมือชื่อของ Notary นี้เป็นของจริง’ และ ‘ตรงตามแบบที่จดทะเบียนกับสภาทนายความ’ เป็นการรับรองตัวผู้รับรอง",
        "Embassy Legalization — สถานทูตประเทศปลายทางรับรองว่า ‘ตราของ MFA ไทยเป็นของจริง’ และเอกสารพร้อมใช้งานในประเทศนั้น ส่วนใหญ่ใช้กับประเทศที่ไม่ใช่สมาชิก Apostille (เช่น UAE จีน เวียดนาม ไต้หวัน)",
        "หลังไทยเข้าร่วม Apostille Convention เมื่อ 30 มิ.ย. 2569 (มีผลบังคับใช้กับไทย 28 ก.พ. 2570) หากปลายทางเป็นประเทศสมาชิก (130 ประเทศ) สามารถลัดขั้นตอน Embassy ได้ — รับ MFA Apostille เพียงครั้งเดียวแล้วใช้งานได้เลย",
      ],
    },
    {
      id: "step-by-step",
      heading: "5. ขั้นตอนการขอ Notary Public แบบละเอียด",
      paragraphs: [
        "ขั้นตอนการขอ Notary ในกรุงเทพฯ และต่างจังหวัดมีรูปแบบมาตรฐานดังนี้ — ทำได้ทั้งแบบ Walk-in, นัดล่วงหน้า หรือออนไลน์ผ่าน Zoom (สำหรับลูกค้าต่างประเทศ)",
      ],
      bullets: [
        "เตรียมต้นฉบับเอกสารและบัตรประชาชน/พาสปอร์ตของผู้ลงนาม",
        "หากเป็นเอกสารภาษาต่างประเทศ จัดทำคำแปลไทย (สำหรับ Notary อ่านเข้าใจ) และคำแปลภาษาปลายทาง",
        "ติดต่อสำนักงานทนายความที่มี Notarial Services Attorney เพื่อนัดวันลงนาม",
        "ลงนามต่อหน้า Notary พร้อมแสดงตัวตน (ห้ามลงนามล่วงหน้า)",
        "Notary ตรวจตัวตน อ่านเนื้อหา และประทับตรา Notary Public พร้อมแบบฟอร์ม Acknowledgment",
        "รับเอกสารพร้อมใบเสร็จและสำเนาสำรองให้ทันที — ใช้เวลารวม 30–60 นาที",
        "หากต้องการส่งต่อ MFA และ Embassy ทาง NYC ให้บริการ One-Stop ใช้เวลารวม 5–10 วันทำการ",
      ],
    },
    {
      id: "pricing",
      heading: "6. ราคา Notary Public ในไทย (ปี 2025)",
      paragraphs: [
        "ราคามาตรฐานในตลาดอยู่ที่ 700–1,500 บาทต่อตราประทับ ขึ้นกับประเภทเอกสาร ความเร่งด่วน และความซับซ้อน NYC คิดค่าบริการตามตารางต่อไปนี้ ซึ่งอยู่ในช่วงเฉลี่ยของตลาดและรวมการตรวจเอกสาร คำปรึกษา และใบเสร็จในนามบริษัทแล้ว",
      ],
      bullets: [
        "Acknowledgment / รับรองลายมือชื่อ — ฿700–฿1,000 ต่อตรา",
        "Certified True Copy — ฿500–฿800 ต่อหน้า",
        "Notarized Translation — ฿1,000–฿1,500 (รวมงานแปลและตรารับรอง)",
        "Affidavit / Statutory Declaration — ฿1,500–฿2,500",
        "Power of Attorney — ฿1,500–฿3,000",
        "Notarization ออนไลน์ (Zoom) — เพิ่ม ฿500",
        "บริการเร่งด่วนภายใน 2 ชั่วโมง — เพิ่ม 50%",
      ],
      callout:
        "ระวังราคาต่ำผิดปกติ (เช่น ฿200–฿300) อาจไม่ใช่ Notarial Services Attorney จริง — ขอเลขทะเบียนสภาทนายความก่อนทุกครั้ง",
    },
    {
      id: "common-mistakes",
      heading: "7. ข้อผิดพลาดที่พบบ่อยและวิธีหลีกเลี่ยง",
      paragraphs: [
        "จากประสบการณ์รับเรื่องลูกค้าที่ถูกปฏิเสธเอกสารโดย Embassy หรือหน่วยงานต่างประเทศ NYC สรุปข้อผิดพลาดที่พบบ่อยและวิธีแก้ไขดังนี้",
      ],
      bullets: [
        "ลงนามล่วงหน้าก่อนพบ Notary — ต้องเซ็นต่อหน้าเท่านั้น มิฉะนั้นเป็นโมฆะ",
        "ใช้ Notary ที่ไม่ขึ้นทะเบียน — Embassy จะปฏิเสธ ต้องเริ่มใหม่",
        "ลืมแปลเป็นภาษาปลายทาง — ทำให้เอกสารใช้ไม่ได้แม้ผ่าน MFA และ Embassy แล้ว",
        "ใช้สำเนาแทนต้นฉบับโดยไม่ผ่าน Certified True Copy ก่อน",
        "เขียนชื่อสะกดไม่ตรงพาสปอร์ต — ต้องสะกดเหมือนกันทุกตัวอักษร",
        "ไม่ระบุชื่อเต็มของผู้รับมอบอำนาจใน POA",
        "เอกสาร MFA Apostille ใช้กับประเทศที่ไม่ใช่สมาชิก Hague — ต้องใช้ Embassy Legalization แทน",
      ],
    },
    {
      id: "by-country",
      heading: "8. การใช้ Notary Public ไทยในต่างประเทศแยกตามประเทศ",
      paragraphs: [
        "ประสบการณ์ของ NYC กับเอกสาร Notary ไทยที่ส่งไปใช้ในต่างประเทศมีรายละเอียดเฉพาะของแต่ละประเทศ ดังนี้",
      ],
      bullets: [
        "สหรัฐอเมริกา — Notary + MFA Apostille (ตั้งแต่ ก.ค. 2025) ใช้ได้ใน 50 รัฐ; ก่อนหน้านี้ต้องผ่าน US Embassy Bangkok",
        "ออสเตรเลีย — Notary + DFAT/Apostille; วีซ่าคู่สมรส subclass 309/100 บังคับ Affidavit ทุกฉบับ",
        "สหราชอาณาจักร — Notary + Apostille (UK เป็นสมาชิก Hague อยู่แล้ว) ใช้ที่ HM Passport Office, Home Office",
        "ญี่ปุ่น — Notary + MFA + Japanese Embassy Bangkok (ญี่ปุ่นยังไม่ทำ Apostille กับไทย ณ ปี 2025)",
        "จีน — Notary + MFA + Chinese Embassy (จีนใช้ Consular Authentication ของตนเอง)",
        "EU/Schengen — Notary + Apostille ใช้ได้ทุกประเทศสมาชิก EU โดยไม่ต้องผ่านสถานทูตอีก",
        "สหรัฐอาหรับเอมิเรตส์ (UAE) — Notary + MFA + UAE Embassy + MOFA Dubai (4 ขั้น)",
      ],
    },
    {
      id: "online-notary",
      heading: "9. Online Notarization สำหรับลูกค้าต่างประเทศ",
      paragraphs: [
        "สำหรับลูกค้าที่ไม่สามารถเดินทางมาประเทศไทยได้ NYC ให้บริการ Online Notarization ผ่าน Zoom พร้อมการบันทึกเซสชั่นและ Identity Verification ขั้นสูง — แต่ต้องเข้าใจว่า “Online Notarization” ในไทยยังไม่ได้รับการรับรองในระดับเดียวกับ Remote Online Notarization (RON) ในรัฐต่างๆ ของสหรัฐ",
        "บริการ Online ของเราจึงใช้ในกรณีที่ปลายทางยอมรับการรับรองด้วยภาพหรือเป็นเอกสารภายในบริษัท เช่น Board Resolution, Internal POA ไม่แนะนำสำหรับเอกสารที่ต้องส่ง Embassy หรือศาลต่างประเทศ — กรณีเหล่านั้นแนะนำให้นัดผ่าน NYC Branch ในกรุงเทพฯ ขอนแก่น อุดรธานี หรือหนองคาย หรือใช้บริการ Notary On-Site (Notary ไปหาที่บ้าน/ออฟฟิศของลูกค้า)",
      ],
    },
    {
      id: "case-studies",
      heading: "10. เคสจริง 3 กรณี",
      paragraphs: [
        "เคส 1 — ครอบครัวคนไทยขายบ้านในสหรัฐอเมริกาผ่าน Power of Attorney ลูกความอยู่ที่กรุงเทพฯ ไม่สะดวกบินไปแคลิฟอร์เนีย NYC จัดทำ POA ภาษาอังกฤษ รับรอง Notary + MFA Apostille (3 วันทำการ) ส่ง FedEx ถึงทนายในแคลิฟอร์เนีย ปิดดีลขายบ้าน USD 1.2M ได้ภายใน 14 วัน",
        "เคส 2 — บริษัท Fintech ในสิงคโปร์ขอ Certificate of Incumbency ของพาร์ทเนอร์ไทยเพื่อเปิดบัญชีธนาคารองค์กร NYC ออก Board Resolution + Articles + Director Affidavit รับรองครบและส่ง Apostille ให้ใน 4 วันทำการ",
        "เคส 3 — คู่หมั้นไทย-อังกฤษเตรียม Spouse Visa UK NYC จัดทำ Affidavit of Single Status, Sponsor Letter, Financial Statement Translation ทั้งหมดผ่าน Notary + Apostille ลูกค้ายื่นวีซ่าผ่านในรอบแรก",
      ],
    },
    {
      id: "why-nyc",
      heading: "11. ทำไมเลือก NYC สำหรับ Notary Public",
      paragraphs: [
        "ทีม Notary Public ของ NYC ประกอบด้วยทนายความที่ขึ้นทะเบียนกับสภาทนายความฯ จำนวน 6 ท่าน โดยทุกท่านมีประสบการณ์ด้านกฎหมายระหว่างประเทศและทำงานกับเอกสารสำหรับ 40+ ประเทศปลายทางมาแล้ว สำนักงานใหญ่ที่ลาดพร้าวเปิดบริการ Walk-in ทุกวันจันทร์-เสาร์ 09:00–18:00 พร้อมสาขาขอนแก่น อุดรธานี หนองคาย และบริการเอกสารทางไปรษณีย์/EMS ทั่วประเทศ",
        "เราเป็นหนึ่งในไม่กี่สำนักงานในไทยที่ให้บริการแบบ One-Stop ครอบคลุม Notary → MFA → Embassy → Translation ในที่เดียว ลดความซับซ้อนและเวลารวมจากปกติ 2–3 สัปดาห์เหลือเฉลี่ย 5–10 วันทำการ พร้อมรับประกันความสำเร็จ — หากเอกสารถูกปฏิเสธจากความผิดพลาดของเรา ทำใหม่ฟรี",
      ],
    },
  ],
  howTo: {
    name: "ขั้นตอนการขอ Notary Public ในประเทศไทย",
    description: "วิธีการขอ Notary Public แบบครบขั้นตอนสำหรับลูกค้าทั่วไป",
    totalTime: "PT1H",
    steps: [
      { name: "เตรียมต้นฉบับและบัตรประจำตัว", text: "นำต้นฉบับเอกสารและบัตรประชาชน/พาสปอร์ตของผู้ลงนามมาให้ Notary ตรวจ", duration: "PT10M" },
      { name: "นัดหมาย Notarial Services Attorney", text: "ติดต่อสำนักงานทนายความที่มีทนายขึ้นทะเบียน Notary", duration: "PT5M" },
      { name: "ลงนามต่อหน้า Notary", text: "ลงนามต่อหน้าทนาย Notary เท่านั้น ห้ามลงนามล่วงหน้า", duration: "PT15M" },
      { name: "ทนายประทับตราและออกใบ Acknowledgment", text: "Notary ตรวจ ลงนาม ประทับตรา และให้เอกสารพร้อมใบเสร็จ", duration: "PT15M" },
      { name: "ส่งต่อ MFA และ Embassy (ถ้าจำเป็น)", text: "เพื่อใช้ในต่างประเทศ ส่งต่อกรมการกงสุลและสถานทูตปลายทาง", duration: "PT5D" },
    ],
  },
  faqs: [
    { q: "Notary Public ในไทยมีอำนาจเท่ากับ Notary ในอเมริกาไหม", a: "ไม่เท่า — Notarial Services Attorney ในไทยเป็นทนายที่ขึ้นทะเบียนเพิ่ม ไม่ใช่อาชีพแยกแบบในอเมริกาหรือยุโรป แต่เอกสารที่รับรองโดย Notary ไทยและผ่าน MFA + Embassy แล้ว ใช้ได้ในระดับสากลเทียบเท่ากัน" },
    { q: "Notary Public ราคาเท่าไหร่", a: "ค่าบริการรับรองลายมือชื่อโดย Notarial Services Attorney ในไทยอยู่ราว 700–1,500 บาทต่อหนึ่งตราประทับ ตัวแปรหลักคือจำนวนลายเซ็นที่ต้องรับรอง ประเภทเอกสาร (affidavit ยาวกว่าคิดสูงกว่าใบมอบอำนาจสั้น) และความเร่งด่วน ค่ารับรองนิติกรณ์ที่กรมการกงสุลและค่าธรรมเนียมสถานทูตปลายทางเป็นคนละส่วน แจ้งใบเสนอราคารายเคสทาง LINE" },
    { q: "เซ็นเอกสารล่วงหน้าได้ไหม", a: "ไม่ได้ ผู้ลงนามต้องลงลายมือชื่อต่อหน้า Notarial Services Attorney พร้อมแสดงพาสปอร์ตหรือบัตรประชาชนตัวจริง เพราะสาระสำคัญของการรับรองคือทนายยืนยันว่าเห็นบุคคลนั้นลงนามด้วยตนเอง เอกสารที่เซ็นมาก่อนแล้วนำมาประทับย้อนหลังถือว่าไม่สมบูรณ์ และมักถูกสถานทูตปลายทางปฏิเสธ" },
    { q: "Notary ออนไลน์ได้ไหม", a: "การรับรองผ่านวิดีโอคอลใช้ได้เฉพาะกรณีที่หน่วยงานปลายทางยอมรับรูปแบบ remote notarization เท่านั้น ในทางปฏิบัติหน่วยงานไทย สถานทูตส่วนใหญ่ และกรมการกงสุลยังต้องการต้นฉบับที่ลงนามต่อหน้า จึงแนะนำนัดพบที่สำนักงานหรือใช้บริการนอกสถานที่แทน เพื่อไม่ให้เอกสารตีกลับกลางทาง" },
    { q: "Affidavit of Single Status ใช้เวลานานไหม", a: "ขั้นตอนรับรองลายมือชื่อโดยทนายใช้เวลาประมาณ 1 วันทำการหลังเอกสารครบ จากนั้นการรับรองนิติกรณ์ที่กรมการกงสุลใช้ 2 วันทำการแบบปกติหรือ 1 วันแบบด่วน และการรับรองต่อที่สถานทูตปลายทางขึ้นกับคิวของแต่ละแห่ง รวมทั้งกระบวนการมักอยู่ที่ 5–10 วันทำการ" },
    { q: "Notary Public อยู่จังหวัดอื่นได้ไหม", a: "ได้ เพราะการขึ้นทะเบียน Notarial Services Attorney กับสภาทนายความมีผลทั่วราชอาณาจักร ไม่ได้จำกัดเขตจังหวัดแบบ Notary ในสหรัฐฯ ผู้ที่อยู่ต่างจังหวัดสามารถนัดหมายล่วงหน้าเพื่อเข้ารับบริการที่สำนักงาน หรือส่งเอกสารและนัดวันลงนามล่วงหน้าเพื่อลดจำนวนรอบเดินทาง" },
    { q: "เอกสารราชการไทยต้อง Notary ก่อนไหม", a: "ไม่ต้อง — ทะเบียนบ้าน บัตรประชาชน ใบเกิด ส่งกรมการกงสุลรับรองนิติกรณ์ได้โดยตรง หลังแปลและรับรองคำแปลแล้ว" },
    { q: "Notary ของไทยเป็น Apostille ไหม", a: "Notary เป็นขั้นแรก ส่วน Apostille ทำที่ MFA หลังจาก 28 ก.พ. 2570 ที่ไทยเข้าเป็นสมาชิก Hague Convention" },
    { q: "ต่างชาติเป็น Notary ในไทยได้ไหม", a: "ไม่ได้ เพราะข้อบังคับสภาทนายความกำหนดให้ผู้ทำหน้าที่ Notarial Services Attorney ต้องเป็นทนายความที่มีใบอนุญาตว่าความในประเทศไทยและผ่านการอบรมขึ้นทะเบียนเพิ่มเติม ซึ่งใบอนุญาตว่าความสงวนไว้สำหรับผู้มีสัญชาติไทย ชาวต่างชาติจึงเป็นได้เพียงผู้ลงนามหรือพยานเท่านั้น" },
    { q: "ตรวจสอบ Notary จริงปลอมที่ไหน", a: "ขอเลขใบอนุญาตว่าความและเลขทะเบียน Notarial Services Attorney จากทนายก่อนทุกครั้ง แล้วตรวจสอบกับสภาทนายความในพระบรมราชูปถัมภ์ที่ lawyerscouncil.or.th หรือโทรสอบถามสำนักงานทะเบียน สังเกตด้วยว่าตราประทับต้องระบุชื่อ-เลขทะเบียนชัดเจน และทนายลงนามต่อหน้าผู้รับบริการจริง" },
  ],
  relatedServiceSlugs: ["notary-public", "mfa-legalization", "embassy-legalization", "translation"],
  citations: [
    { name: "สภาทนายความในพระบรมราชูปถัมภ์", url: "https://www.lawyerscouncil.or.th" },
    { name: "ข้อบังคับสภาทนายความฯ ปี 2551", url: "https://www.lawyerscouncil.or.th" },
    { name: "กรมการกงสุล กระทรวงการต่างประเทศ", url: "https://consular.mfa.go.th" },
    { name: "HCCH Apostille Convention", url: "https://www.hcch.net/en/instruments/conventions/full-text/?cid=41" },
  ],
};

// ───────────────────────────────────────────────────────────────
// GUIDE 2 — NAATI CERTIFIED TRANSLATION
// ───────────────────────────────────────────────────────────────
const NAATI: Guide = {
  slug: "naati-translation-thailand",
  category: "NAATI",
  title: "NAATI Certified Translation จากประเทศไทย 2025 — Ultimate Guide | NYC",
  h1: "NAATI Translation จากประเทศไทย — Ultimate Guide สำหรับวีซ่าออสเตรเลีย",
  tldr:
    "NAATI (National Accreditation Authority for Translators and Interpreters) คือหน่วยงานเดียวของรัฐบาลออสเตรเลียที่รับรองนักแปล/ล่าม วีซ่าออสเตรเลียทุกประเภท (Visitor, Student, Spouse, Skilled, PR) บังคับเอกสารที่ไม่ใช่ภาษาอังกฤษให้แปลโดย NAATI Certified Translator เท่านั้น ราคา NAATI ในไทย 1,300–2,500 บาทต่อหน้า ใช้เวลา 1–3 วัน NYC มีนักแปล NAATI ในทีม 8 ท่าน ครอบคลุม Thai/Lao/Burmese/Khmer/Vietnamese/Chinese ↔ English",
  metaDesc:
    "คู่มือ NAATI Certified Translation ฉบับสมบูรณ์ — สำหรับยื่นวีซ่าออสเตรเลีย Student/Spouse/Skilled/PR ราคา ขั้นตอน ตัวอย่างเอกสาร 30 ประเภท พร้อมรายชื่อนักแปล NAATI ในไทย",
  keywords: ["NAATI", "NAATI translation", "นักแปล NAATI", "วีซ่าออสเตรเลีย แปลเอกสาร", "naati certified translator thailand", "AAT309", "spouse visa australia"],
  readMinutes: 18,
  updated: "2026-05-15",
  intro:
    "การยื่นวีซ่าออสเตรเลียทุกประเภท ตั้งแต่ Visitor Visa (subclass 600) ไปจนถึง Permanent Residence และ Citizenship บังคับให้เอกสารที่ไม่ใช่ภาษาอังกฤษต้องแปลโดยนักแปลที่ได้รับการรับรองจาก NAATI — National Accreditation Authority for Translators and Interpreters ซึ่งเป็นหน่วยงานเดียวของรัฐบาลออสเตรเลียที่มีอำนาจรับรองนักแปลและล่าม คู่มือฉบับนี้รวบรวมทุกสิ่งที่คนไทยและชาวต่างชาติในไทยต้องรู้เกี่ยวกับ NAATI Translation รวมถึงระดับการรับรอง (Certified, Specialist, Recognised), เอกสารที่ต้องแปล, ราคามาตรฐานในไทย, ความผิดพลาดที่ทำให้วีซ่าถูกปฏิเสธ และวิธีตรวจสอบนักแปล NAATI",
  sections: [
    {
      id: "what-is-naati",
      heading: "1. NAATI คืออะไร",
      paragraphs: [
        "NAATI (National Accreditation Authority for Translators and Interpreters) ก่อตั้งปี 1977 เป็นหน่วยงานในกำกับของรัฐบาลกลางและรัฐต่างๆ ของออสเตรเลีย มีอำนาจรับรองนักแปล (Translators) และล่าม (Interpreters) ใน 160+ คู่ภาษาทั่วโลก เพื่อให้บริการทางการแปลที่มีมาตรฐานสำหรับหน่วยงานราชการ ศาล โรงพยาบาล และกิจการที่เกี่ยวข้องกับวีซ่าและการย้ายถิ่น",
        "การรับรอง NAATI แบ่งเป็นหลายระดับ — Recognised Practising (ภาษาที่ทดสอบไม่ได้), Certified Provisional, Certified Translator, Certified Advanced Translator และ Certified Specialist Translator (Legal/Medical/Scientific) แต่ละระดับมีตราประทับและหมายเลข Practitioner ID เฉพาะตัว ตรวจสอบได้ที่ www.naati.com.au",
      ],
    },
    {
      id: "why-required",
      heading: "2. ทำไมวีซ่าออสเตรเลียต้องใช้ NAATI",
      paragraphs: [
        "Department of Home Affairs ของออสเตรเลีย (เดิม DIBP) กำหนดในนโยบาย PAM3 (Procedures Advice Manual) และ ImmiAccount Guidelines ว่าเอกสารที่ไม่ใช่ภาษาอังกฤษทุกฉบับที่ยื่นประกอบวีซ่าต้องแปลโดย NAATI Certified Translator เท่านั้น คำแปลโดยนักแปลทั่วไปหรือผู้เชี่ยวชาญที่ไม่มี NAATI จะไม่ได้รับการยอมรับและทำให้ใบสมัครถูกปฏิเสธหรือต้องแปลซ้ำ",
        "เหตุผลหลักคือ NAATI เป็นมาตรฐานเดียวที่รัฐบาลออสเตรเลียควบคุมคุณภาพได้โดยตรง — มีระบบสอบ การต่ออายุทุก 3 ปี การอบรม Continued Professional Development (CPD) และระเบียบจรรยาบรรณที่ลงโทษได้จริงหากนักแปลทำผิด",
      ],
    },
    {
      id: "documents-list",
      heading: "3. เอกสารที่ต้องแปลด้วย NAATI สำหรับวีซ่าออสเตรเลีย",
      paragraphs: ["รายการเอกสารที่ NYC แปล NAATI ให้ลูกค้าวีซ่าออสเตรเลียบ่อยที่สุด แบ่งตามประเภทวีซ่า"],
      bullets: [
        "Student Visa (subclass 500) — ทรานสคริปต์, ใบปริญญา, ใบรับรองผลภาษาอังกฤษ, Sponsor Affidavit, Bank Statement",
        "Spouse Visa (subclass 309/100, 820/801) — ทะเบียนสมรส, ทะเบียนหย่า (ถ้ามี), ทะเบียนบ้าน, ใบเกิดบุตร, สูติบัตร, Affidavit ความสัมพันธ์",
        "Skilled Visa (subclass 189/190/491) — ใบปริญญา, ทรานสคริปต์, หนังสือรับรองการทำงาน, JD, Sponsor Letter",
        "Visitor Visa (subclass 600) — Bank Statement, หนังสือรับรองการทำงาน, สลิปเงินเดือน, ทะเบียนบ้าน",
        "Parent Visa (subclass 103/143) — ทะเบียนบ้าน, สูติบัตร, ใบรับรองความสัมพันธ์",
        "Working Holiday (subclass 417/462) — ทรานสคริปต์, ใบรับรองการศึกษา (ถ้าต้องการ)",
        "Citizenship — ทะเบียนบ้าน, สูติบัตร, ทะเบียนสมรส (ถ้ายังไม่เคยแปลส่ง)",
      ],
    },
    {
      id: "pricing",
      heading: "4. ราคา NAATI ในไทย (อัปเดตปี 2025)",
      paragraphs: [
        "ราคา NAATI Translation จากไทยจะถูกกว่าการแปลในออสเตรเลียประมาณ 60–70% เนื่องจากค่าครองชีพและค่าแรงต่างกัน คุณภาพและการยอมรับเท่ากันทุกประการ เพราะตราประทับ NAATI ออกโดยนักแปลในออสเตรเลีย ไม่จำกัดสถานที่ทำงาน",
      ],
      bullets: [
        "Standard NAATI Translation — ฿1,300–฿2,500 ต่อหน้า A4 (มาตรฐาน 250 คำ)",
        "เอกสารทางการ (ทะเบียนบ้าน สูติบัตร ทะเบียนสมรส) — ฿900–฿1,500 ต่อฉบับ",
        "เอกสารทางวิชาการ (ทรานสคริปต์มหาวิทยาลัย) — ฿1,200–฿1,800 ต่อฉบับ",
        "เอกสารกฎหมาย (Affidavit, สัญญา) — ฿1,500–฿2,500 ต่อหน้า",
        "บริการเร่งด่วน 24 ชม. — เพิ่ม 50%",
        "ส่งไฟล์ Soft Copy PDF ใช้ได้กับ ImmiAccount ทันที — ฟรี",
      ],
    },
    {
      id: "how-it-works",
      heading: "5. ขั้นตอนการแปล NAATI ที่ NYC",
      paragraphs: [],
      bullets: [
        "ส่งสแกนเอกสารชัดเจน (PDF/JPG) มาทาง LINE @nycli หรืออีเมล",
        "ทีมประเมินจำนวนหน้าและแจ้งราคา/เวลาใน 30 นาที",
        "ชำระมัดจำ 50% โอนเข้าบัญชีบริษัท NYC Translation Co., Ltd.",
        "นักแปล NAATI Certified ดำเนินการแปลและรับรอง (1–3 วันทำการ)",
        "ส่งไฟล์ PDF ที่มีตรา NAATI พร้อม Practitioner ID + Statement of Truth",
        "หากต้องการต้นฉบับลายเซ็นจริง รับได้ที่สำนักงาน NYC หรือจัดส่ง EMS",
      ],
    },
    {
      id: "common-mistakes",
      heading: "6. ข้อผิดพลาดที่ทำให้วีซ่าถูกปฏิเสธ",
      paragraphs: [],
      bullets: [
        "ใช้นักแปลที่ไม่มี NAATI Practitioner ID — ตรวจสอบที่ www.naati.com.au",
        "ตราประทับ NAATI หมดอายุ (NAATI ต้อง renew ทุก 3 ปี)",
        "สแกนเอกสารต้นฉบับไม่ชัด — Home Affairs ปฏิเสธ",
        "ลืมแปลตราประทับและลายเซ็นในเอกสารต้นฉบับ — ต้องแปลทั้งหน้า ไม่ใช่เฉพาะข้อความ",
        "ใช้คำแปลที่ไม่ตรงพาสปอร์ต (สะกดชื่อต่างกัน)",
        "แปล Bank Statement แล้วยอดเงินไม่ตรงต้นฉบับ — เกิดจากการพิมพ์ผิด",
      ],
    },
    {
      id: "naati-vs-others",
      heading: "7. NAATI vs. Certified Translation อื่นๆ",
      paragraphs: [
        "หลายลูกค้าสับสนระหว่าง NAATI กับการรับรองอื่นๆ — ทั้ง Sworn Translation, ATA (อเมริกา), CTTIC (แคนาดา), ITI (UK) — แม้จะเป็นการรับรองทางการแปลทั้งหมด แต่แต่ละมาตรฐานมีขอบเขตการใช้งานต่างกัน",
        "สำหรับวีซ่าออสเตรเลียเท่านั้นที่ต้อง NAATI — สำหรับสหรัฐใช้ ATA Certified, สำหรับยุโรปใช้ Sworn Translator ของแต่ละประเทศ, สำหรับสหราชอาณาจักรใช้ ITI หรือ Certified Translator + Notary",
      ],
    },
    {
      id: "languages",
      heading: "8. ภาษาที่ NYC ให้บริการ NAATI",
      paragraphs: [],
      bullets: [
        "Thai ↔ English (หลัก)",
        "Lao ↔ English",
        "Burmese ↔ English",
        "Khmer ↔ English",
        "Vietnamese ↔ English",
        "Chinese (Simplified/Traditional) ↔ English",
        "Japanese, Korean — ผ่านพาร์ทเนอร์ NAATI ในซิดนีย์/เมลเบิร์น",
      ],
    },
    {
      id: "case-studies",
      heading: "9. เคสจริง",
      paragraphs: [
        "เคส 1 — นักเรียนไทยยื่น Student Visa subclass 500 ไปเรียน Master ที่ University of Melbourne NYC แปลทรานสคริปต์ปริญญาตรี + ใบรับรองภาษาอังกฤษ + Sponsor Affidavit ภายใน 2 วัน วีซ่าออกใน 18 วัน",
        "เคส 2 — คู่สมรสไทย-ออสซี่ ยื่น Partner Visa subclass 309 NYC แปลทะเบียนสมรส, ทะเบียนบ้าน, ใบเกิดบุตร, Affidavit จากเพื่อน 4 คน NAATI ทั้งหมด เคสผ่านในรอบแรกหลัง 11 เดือน",
        "เคส 3 — ผู้สมัคร Skilled 189 สาขา Software Engineer NYC แปลใบปริญญา + ทรานสคริปต์ + หนังสือรับรองทำงาน 3 บริษัท + JD ภายใน 4 วัน ผู้สมัครได้ Invitation จาก SkillSelect",
      ],
    },
    {
      id: "why-nyc",
      heading: "10. ทำไมเลือก NYC สำหรับ NAATI",
      paragraphs: [
        "NYC เป็นสำนักงานในไทยที่มีนักแปล NAATI Certified ประจำในทีม 8 ท่าน ครอบคลุม Thai/Lao/Burmese/Khmer/Vietnamese/Chinese ↔ English พร้อม Quality Control ภายในและการตรวจซ้ำโดยนักแปลคนที่สองก่อนส่งให้ลูกค้าทุกครั้ง — ลดความผิดพลาดจากการสะกดชื่อหรือยอดเงินไม่ตรงต้นฉบับ ซึ่งเป็นสาเหตุหลักของการปฏิเสธวีซ่า",
        "ลูกค้า NAATI ของ NYC กว่า 95% ผ่านวีซ่าออสเตรเลียในรอบแรก และเรารับประกันงานแปลทุกฉบับ — หาก Home Affairs ปฏิเสธเอกสารเพราะคุณภาพการแปล เราแปลใหม่ฟรีและคืนค่าบริการ",
      ],
    },
  ],
  howTo: {
    name: "วิธีแปล NAATI สำหรับวีซ่าออสเตรเลีย",
    description: "ขั้นตอนการแปลเอกสารด้วย NAATI Certified Translator",
    totalTime: "PT72H",
    steps: [
      { name: "สแกนเอกสารต้นฉบับ", text: "สแกนชัดเจน รูปแบบ PDF หรือ JPG ความละเอียดสูง", duration: "PT15M" },
      { name: "ส่งให้ NYC ประเมิน", text: "ส่ง LINE @nycli หรืออีเมล ทีมประเมินใน 30 นาที", duration: "PT30M" },
      { name: "ชำระมัดจำและรอแปล", text: "นักแปล NAATI แปลและรับรอง", duration: "PT48H" },
      { name: "รับไฟล์ PDF พร้อมตรา NAATI", text: "ใช้ส่ง ImmiAccount ได้ทันที", duration: "PT15M" },
    ],
  },
  faqs: [
    { q: "NAATI ใช้ได้กับวีซ่าประเทศไหนบ้าง", a: "NAATI เป็นระบบรับรองนักแปลของออสเตรเลีย จึงเกี่ยวข้องโดยตรงกับคำร้องที่ยื่นต่อ Department of Home Affairs และหน่วยงานออสเตรเลียอื่น ส่วนนิวซีแลนด์ระบุให้ใช้ผู้แปลที่เป็นกลางและมีคุณวุฒิ ซึ่งคำแปล NAATI เข้าเกณฑ์ ประเทศอื่นไม่ได้บังคับ NAATI แต่มักยอมรับเพราะคำแปลระบุตัวตนและคุณวุฒิผู้แปลครบถ้วน" },
    { q: "นักแปล NAATI ในไทยมีไหม", a: "มี และไม่จำเป็นต้องบินไปออสเตรเลียเพื่อใช้บริการ เพราะ NAATI รับรองที่ตัวนักแปลไม่ใช่ที่สถานที่ทำงาน ทีมของเราทำงานคู่ระหว่างกรุงเทพฯ กับผู้แปลที่ถือหมายเลข NAATI ครอบคลุมคู่ภาษาไทย–อังกฤษเป็นหลัก จุดสำคัญคือขอดูหมายเลขรับรองและวันหมดอายุบนคำแปลก่อนยื่นเสมอ" },
    { q: "ราคา NAATI ในไทยเท่าไหร่", a: "ค่าแปลรับรอง NAATI คิดตามหน้าและความหนาแน่นของข้อความ เอกสารทะเบียนราษฎรแบบหน้าเดียว เช่น สูติบัตร ทะเบียนบ้าน หรือทะเบียนสมรส เริ่มต้นหลักพันบาทต่อฉบับ ส่วนเอกสารเชิงเทคนิคหรือคำพิพากษาที่มีศัพท์เฉพาะจะสูงกว่า ขอใบเสนอราคาแบบระบุจำนวนหน้าทาง LINE ได้ก่อนตัดสินใจ" },
    { q: "ใช้เวลานานแค่ไหน", a: "งานแปลรับรอง NAATI ของเอกสารมาตรฐาน 1–3 ฉบับ ปกติใช้เวลา 1–3 วันทำการนับจากได้รับไฟล์สแกนที่อ่านออกครบทุกมุมและยืนยันการสะกดชื่อตามพาสปอร์ต ชุดเอกสารจำนวนมากหรือเอกสารเทคนิคใช้เวลามากกว่านั้น กรณีเร่งด่วนมีค่าบริการเพิ่มและต้องเช็กคิวผู้แปลก่อนยืนยัน" },
    { q: "ต้องส่งต้นฉบับให้ NYC ไหม", a: "ไม่ต้องในขั้นตอนแปล เพราะผู้แปล NAATI รับรองจากสำเนาที่ได้รับและระบุไว้บนคำแปลว่าแปลจากเอกสารรูปแบบใด สแกนสีความละเอียดสูงที่เห็นตราประทับและเลขที่เอกสารชัดจึงเพียงพอ แต่ต้องเก็บต้นฉบับไว้แสดงต่อหน่วยงานปลายทาง และหากต้องรับรองนิติกรณ์เพิ่มจึงค่อยใช้ตัวจริง" },
    { q: "ตราประทับ NAATI หมดอายุไหม", a: "ตัวการรับรองของผู้แปลมีอายุและต้องต่ออายุตามรอบที่ NAATI กำหนดภายใต้ระบบ recertification ส่วนคำแปลที่ออกในช่วงที่ผู้แปลยังมีสถานะ certified ถือว่าสมบูรณ์ตลอดไป อย่างไรก็ตามหน่วยงานปลายทางบางแห่งขอคำแปลที่ออกใหม่ไม่เกิน 3–6 เดือน จึงควรอ่านเงื่อนไขของคำร้องก่อน" },
    { q: "ถ้าวีซ่าโดนปฏิเสธเพราะคำแปลล่ะ", a: "กรณีหน่วยงานระบุเป็นลายลักษณ์อักษรว่าปัญหาอยู่ที่คำแปล เช่น ขาดคำรับรอง สะกดชื่อไม่ตรงพาสปอร์ต หรือแปลไม่ครบหน้า เราตรวจสอบและแก้ไขคำแปลฉบับนั้นให้ตรงตามข้อทักท้วงโดยไม่คิดค่าแปลซ้ำ ทั้งนี้ผลการพิจารณาวีซ่าเป็นดุลพินิจของหน่วยงานปลายทาง ไม่มีผู้ให้บริการรายใดรับประกันผลได้" },
    { q: "NAATI ต่างจาก Sworn Translator ยังไง", a: "NAATI คือระบบรับรองคุณวุฒิผู้แปลของออสเตรเลีย ขณะที่ sworn translator คือผู้แปลที่สาบานตนต่อศาลหรือขึ้นทะเบียนกับหน่วยงานรัฐในประเทศระบบซีวิลลอว์ เช่น ฝรั่งเศส เยอรมนี สเปน ส่วนสหรัฐฯ ไม่มีระบบรัฐรับรอง จึงใช้ certified translation แนบ affidavit ของผู้แปลแทน" },
  ],
  relatedServiceSlugs: ["naati", "translation", "visa-thai-citizens"],
  citations: [
    { name: "NAATI Official", url: "https://www.naati.com.au" },
    { name: "Department of Home Affairs", url: "https://immi.homeaffairs.gov.au" },
    { name: "PAM3 — Translation Requirements", url: "https://immi.homeaffairs.gov.au/help-support/glossary" },
  ],
};

// ───────────────────────────────────────────────────────────────
// GUIDE 3 — MFA LEGALIZATION + APOSTILLE
// ───────────────────────────────────────────────────────────────
const MFA: Guide = {
  slug: "mfa-legalization-apostille-thailand",
  category: "Legalization",
  title: "รับรองกงสุล MFA + Apostille ประเทศไทย 2025 — Ultimate Guide | NYC",
  h1: "MFA Legalization + Apostille — Ultimate Guide ฉบับปี 2025",
  tldr:
    "กรมการกงสุล กระทรวงการต่างประเทศ (MFA) รับรองนิติกรณ์เอกสารไทย 4 ประเภท: ตราประทับและลายมือชื่อ, สำเนาถูกต้อง, คำแปล, และตรา Apostille (เริ่ม 28 ก.พ. 2570) ราคา ฿200/ตรา ใช้เวลา 2 วันทำการ (Walk-in) หรือ 3-7 วัน (Bangkok Online) — สำหรับเอกสารใช้ในประเทศสมาชิก Hague 130 ประเทศ Apostille ใช้แทน Embassy Legalization ได้",
  metaDesc:
    "คู่มือรับรองกงสุล MFA + Apostille ครบ — Walk-in vs. ออนไลน์, เอกสารที่รับรองได้, ราคาทางการ, ขั้นตอน Apostille หลังไทยเข้า Hague Convention 2025, ความต่างกับ Embassy Legalization",
  keywords: ["MFA legalization", "รับรองกงสุล", "นิติกรณ์", "apostille thailand", "กรมการกงสุล", "embassy legalization", "consular legalization"],
  readMinutes: 16,
  updated: "2026-05-15",
  intro:
    "การรับรองเอกสารโดยกรมการกงสุล กระทรวงการต่างประเทศ (MFA Legalization หรือที่คนทั่วไปเรียก ‘รับรองนิติกรณ์’) เป็นขั้นตอนกลางที่เชื่อมระหว่างเอกสารต้นฉบับของไทย (หรือคำแปลที่ผ่าน Notary แล้ว) กับการใช้งานในต่างประเทศ การที่ประเทศไทยเข้าเป็นสมาชิก Hague Apostille Convention อย่างเป็นทางการในเดือนกรกฎาคม 2025 ทำให้ขั้นตอนนี้สำคัญและซับซ้อนน้อยลงสำหรับลูกค้าที่จะใช้เอกสารใน 130 ประเทศสมาชิก แต่หลายคนยังไม่เข้าใจว่า Apostille ต่างจาก Legalization แบบเดิมอย่างไร และเมื่อไหร่ต้องใช้แบบไหน",
  sections: [
    {
      id: "what-is-mfa",
      heading: "1. MFA Legalization คืออะไร",
      paragraphs: [
        "การรับรองนิติกรณ์โดยกรมการกงสุล (Consular Affairs Department) คือกระบวนการที่กระทรวงการต่างประเทศของไทยยืนยันว่าตราประทับ ลายมือชื่อ หรือคำแปลในเอกสารเป็นของจริงและตรงตามแบบที่ขึ้นทะเบียนไว้ — ทำให้เอกสารสามารถส่งต่อสถานทูตประเทศปลายทางเพื่อรับรองอีกชั้นและใช้ได้ในต่างประเทศ",
        "เอกสารที่กรมการกงสุลรับรองได้แบ่งเป็น 4 กลุ่มหลัก: (1) เอกสารราชการไทย เช่น ทะเบียนบ้าน บัตรประชาชน สูติบัตร ใบเกิด ใบหย่า ใบเปลี่ยนชื่อ; (2) คำแปลของเอกสารราชการ; (3) เอกสารที่ผ่าน Notary Public มาแล้ว เช่น Affidavit, POA; (4) เอกสารบริษัทที่ออกโดย DBD (กรมพัฒนาธุรกิจการค้า)",
      ],
    },
    {
      id: "apostille-vs-legalization",
      heading: "2. Apostille vs. Legalization — ต่างกันอย่างไร",
      paragraphs: [
        "Apostille คือใบรับรองสากลที่ออกตาม Hague Convention 1961 มีรูปแบบมาตรฐานเดียวกัน 10 ช่อง ใช้แทน Embassy Legalization ได้ในประเทศสมาชิก 130 ประเทศ — ประหยัดเวลา ค่าใช้จ่าย และไม่ต้องเดินทางหลายหน่วยงาน",
        "Legalization (แบบเดิม) คือ Consular Legalization ซึ่งต้องผ่านสองขั้น: MFA ไทย → สถานทูตประเทศปลายทาง ใช้กับประเทศที่ไม่ใช่สมาชิก Hague เช่น UAE, จีน, เวียดนาม, ไต้หวัน, ลาว, กัมพูชา",
        "ประเทศไทยเข้าเป็นสมาชิก Hague Convention วันที่ 30 มิถุนายน 2569 และมีผลบังคับใช้ 28 กุมภาพันธ์ 2570 — หลังจากวันนี้ ผู้ที่จะใช้เอกสารใน EU, US, UK, ออสเตรเลีย, ญี่ปุ่น (เร็วๆ นี้) ฯลฯ จะใช้ MFA Apostille แทน Consular Legalization ได้",
      ],
      callout: "ตรวจรายชื่อประเทศสมาชิก Hague Convention ได้ที่ www.hcch.net — ไม่ใช่ทุกประเทศที่เป็นสมาชิก",
    },
    {
      id: "pricing-official",
      heading: "3. ค่าธรรมเนียมทางการ",
      paragraphs: ["อัตราค่าธรรมเนียมของกรมการกงสุล (ตามประกาศกระทรวงการต่างประเทศ)"],
      bullets: [
        "บริการธรรมดา (Walk-in) — ฿200/ตรา รับใน 2 วันทำการ",
        "บริการด่วน (Walk-in) — ฿400/ตรา รับในวันเดียวกัน",
        "บริการออนไลน์ (Legalize Online) — ฿200/ตรา รับใน 3-7 วันทำการ",
        "ค่าจัดส่งทางไปรษณีย์ (กรมจัดส่งให้) — ฿120",
        "Apostille — ค่าธรรมเนียมเดียวกับนิติกรณ์ปกติ",
      ],
      callout: "ราคาเหล่านี้เป็นค่าธรรมเนียมของรัฐบาล — ไม่รวมค่าบริการของบริษัทที่ดำเนินการแทน",
    },
    {
      id: "where-to-apply",
      heading: "4. สถานที่ให้บริการ",
      paragraphs: [
        "กรมการกงสุลมีสถานที่ให้บริการ 4 จุดหลัก:",
      ],
      bullets: [
        "สำนักงานใหญ่ ถนนแจ้งวัฒนะ (ใกล้ศูนย์ราชการ) — รับทุกประเภท",
        "MFA หลักสี่ (อาคารกระทรวงเดิม) — ปิดปรับปรุง ณ ปี 2025",
        "MFA เมืองทอง — สำหรับ Apostille โดยเฉพาะ",
        "ระบบ Legalize Online (https://consular.mfa.go.th) — ส่งทางไปรษณีย์",
        "สำนักงานหนังสือเดินทางในต่างจังหวัด — รับยื่นแทนได้บางประเภท",
      ],
    },
    {
      id: "how-it-works",
      heading: "5. ขั้นตอนการขอ MFA Legalization",
      paragraphs: [],
      bullets: [
        "เตรียมเอกสารต้นฉบับ + สำเนา + คำแปลที่รับรองโดยนักแปลที่ขึ้นทะเบียน (หากเป็นเอกสารราชการไทย)",
        "หากเป็น Affidavit/POA ต้องผ่าน Notary ก่อน",
        "ยื่นที่ MFA แจ้งวัฒนะ หรือผ่านระบบ Legalize Online",
        "ชำระค่าธรรมเนียมที่เคาน์เตอร์ (เงินสด/QR)",
        "รับเอกสาร (ธรรมดา 2 วัน ด่วน 1 วัน) — มี SMS แจ้ง",
        "หากต้องส่งต่อสถานทูต ดำเนินการต่อภายใน 6 เดือน",
      ],
    },
    {
      id: "common-documents",
      heading: "6. เอกสารที่ขอรับรองบ่อย",
      paragraphs: [],
      bullets: [
        "ทะเบียนบ้าน + คำแปลภาษาอังกฤษ",
        "บัตรประชาชน + คำแปล",
        "สูติบัตร + คำแปล",
        "ทะเบียนสมรส + คำแปล (สำหรับวีซ่าคู่สมรส)",
        "ใบหย่า + คำแปล",
        "ใบเปลี่ยนชื่อ-สกุล + คำแปล",
        "Police Clearance + คำแปล",
        "หนังสือรับรอง DBD (บริษัท) + คำแปล",
        "Affidavit / POA (ผ่าน Notary)",
      ],
    },
    {
      id: "embassy-step",
      heading: "7. การส่งต่อสถานทูต (Embassy Legalization)",
      paragraphs: [
        "หลังเอกสารผ่าน MFA แล้ว และประเทศปลายทางไม่ใช่สมาชิก Hague Apostille ต้องส่งต่อสถานทูตของประเทศนั้นในกรุงเทพฯ เพื่อรับรองอีกชั้น เรียกว่า Consular Legalization สถานทูตที่ NYC ให้บริการเดินเอกสารบ่อย ได้แก่:",
      ],
      bullets: [
        "China — ใช้ผ่าน CVASC (China Visa Application Service Center)",
        "UAE — ผ่าน UAE Embassy Bangkok + MOFA Dubai",
        "เวียดนาม — Vietnamese Embassy Bangkok",
        "ลาว / กัมพูชา / เมียนมา — สถานทูตในกรุงเทพฯ",
        "ไต้หวัน — Taipei Economic and Cultural Office",
        "ซาอุดิอาระเบีย / กาตาร์ / โอมาน — Embassy + Foreign Office ของประเทศนั้น",
      ],
    },
    {
      id: "common-issues",
      heading: "8. ปัญหาที่พบบ่อย",
      paragraphs: [],
      bullets: [
        "คำแปลใช้นักแปลที่ไม่ขึ้นทะเบียน MFA — กรมการกงสุลปฏิเสธ",
        "สะกดชื่อในคำแปลไม่ตรงกับพาสปอร์ต — ต้องแก้ใหม่",
        "เอกสารต้นฉบับเก่ามาก — บางครั้งต้องคัดสำเนาใหม่ที่อำเภอก่อน",
        "Apostille ใช้กับประเทศที่ไม่ใช่สมาชิก Hague — ไม่ได้ผล ต้องใช้ Consular Legalization",
        "เกินอายุ 6 เดือนหลัง MFA — สถานทูตอาจปฏิเสธ ต้องเริ่มใหม่",
      ],
    },
    {
      id: "nyc-service",
      heading: "9. บริการ One-Stop ของ NYC",
      paragraphs: [
        "NYC เป็นหนึ่งในไม่กี่บริษัทในไทยที่มีนักแปลขึ้นทะเบียน MFA, ทีมทนาย Notary 6 ท่าน, และทีมเดินเอกสารประจำที่ MFA แจ้งวัฒนะและสถานทูต 20+ แห่ง — ทำให้สามารถดำเนินการแปล → Notary → MFA → Embassy ในที่เดียว ลดเวลาจากปกติ 3–4 สัปดาห์เหลือ 7–14 วันทำการ พร้อมรับ-ส่งเอกสารทั่วกรุงเทพฯ และทั่วประเทศผ่าน EMS",
      ],
    },
  ],
  howTo: {
    name: "ขั้นตอน MFA Legalization + Apostille",
    description: "วิธีการขอรับรองเอกสารโดยกรมการกงสุล",
    totalTime: "PT48H",
    steps: [
      { name: "เตรียมเอกสาร + คำแปล", text: "ต้นฉบับ + คำแปลโดยนักแปลขึ้นทะเบียน MFA", duration: "PT2H" },
      { name: "ยื่นที่ MFA แจ้งวัฒนะ หรือออนไลน์", text: "เลือก Walk-in 2 วัน หรือ Online 3-7 วัน", duration: "PT1H" },
      { name: "ชำระค่าธรรมเนียม ฿200/ตรา", text: "ราคาทางการ ไม่รวมค่าบริการบริษัท", duration: "PT15M" },
      { name: "รับเอกสารกลับ", text: "Walk-in 2 วัน / ด่วน วันเดียว / Online ทางไปรษณีย์", duration: "PT48H" },
      { name: "ส่งต่อสถานทูต (ถ้าจำเป็น)", text: "หากปลายทางไม่ใช่ Hague — ส่ง Embassy", duration: "PT72H" },
    ],
  },
  faqs: [
    { q: "MFA Legalization ราคาเท่าไหร่", a: "ค่าธรรมเนียมรัฐบาล ฿200/ตรา (ธรรมดา) ฿400/ตรา (ด่วน) NYC คิดค่าบริการเดินเอกสารเพิ่ม ฿500-1,500" },
    { q: "Apostille ต้องทำใน MFA ไหม", a: "ใช่ กรมการกงสุล กระทรวงการต่างประเทศ จะเป็น Competent Authority ผู้ออก Apostille ของประเทศไทย โดยอนุสัญญา Apostille จะมีผลกับไทยในวันที่ 28 กุมภาพันธ์ 2570 ระหว่างนี้เอกสารไทยยังต้องผ่านการรับรองนิติกรณ์ที่กรมการกงสุลแล้วรับรองต่อที่สถานทูตปลายทางตามขั้นตอนเดิม" },
    { q: "Walk-in ใช้เวลาเท่าไหร่", a: "การยื่นด้วยตนเองที่กรมการกงสุลใช้เวลาประมาณ 2 วันทำการสำหรับบริการปกติ และ 1 วันทำการสำหรับบริการด่วนที่เสียค่าธรรมเนียมสูงกว่า ควรเผื่อเวลาต่อคิวช่วงเช้าและตรวจว่าคำแปลกับต้นฉบับครบชุด เพราะเอกสารที่ขาดหน้าหรือแปลผิดจะถูกตีกลับให้แก้ก่อนเริ่มนับรอบใหม่" },
    { q: "Apostille ใช้ได้กี่ประเทศ", a: "อนุสัญญา Apostille มีภาคีประมาณ 130 ประเทศและเขตแดน ครอบคลุมสหภาพยุโรป สหราชอาณาจักร สหรัฐอเมริกา ออสเตรเลีย นิวซีแลนด์ ญี่ปุ่น เกาหลีใต้ จีน และอินเดีย จำนวนภาคีเพิ่มขึ้นเรื่อย ๆ จึงควรตรวจสถานะล่าสุดของประเทศปลายทางจากเว็บไซต์ HCCH ก่อนวางแผนเอกสาร" },
    { q: "เอกสารคำแปลใช้ใครก็ได้ไหม", a: "ไม่ได้ กรมการกงสุลรับรองคำแปลเฉพาะงานที่แปลถูกต้องครบถ้วนตรงกับต้นฉบับและมีผู้แปลรับรองตนเองตามรูปแบบที่กำหนด คำแปลที่ตัดข้อความ ใส่ตราประทับไม่ครบ หรือสะกดชื่อไม่ตรงพาสปอร์ต จะถูกตีกลับ จึงควรใช้ผู้แปลที่ทำงานกับกรมการกงสุลเป็นประจำ" },
    { q: "ส่งทางไปรษณีย์ได้ไหม", a: "ได้ กรมการกงสุลเปิดรับคำร้องรับรองนิติกรณ์ทางไปรษณีย์และผ่านระบบออนไลน์ โดยผู้ยื่นต้องแนบเอกสารต้นฉบับ คำแปล สำเนาบัตรประชาชนหรือพาสปอร์ต และซองส่งกลับ ระยะเวลาจะนานกว่าการยื่นด้วยตนเองเพราะรวมเวลาขนส่ง หรือจะมอบอำนาจให้ตัวแทนเดินเรื่องแทนก็ได้" },
    { q: "MFA นานแค่ไหนใช้ได้ก่อนหมดอายุ", a: "ตราประทับรับรองนิติกรณ์ของกรมการกงสุลไม่ได้กำหนดวันหมดอายุไว้ในตัวเอง แต่หน่วยงานปลายทางมักกำหนดเอง เช่น สถานทูตและสำนักทะเบียนหลายแห่งรับเอกสารที่ออกไม่เกิน 3–6 เดือน โดยเฉพาะหนังสือรับรองความเป็นโสดและใบรับรองความประพฤติ จึงควรจัดลำดับให้ยื่นปลายทางเร็วที่สุด" },
    { q: "ประเทศที่ต้องใช้ Consular Legalization แทน Apostille", a: "ประเทศที่ยังไม่เป็นภาคีอนุสัญญา Apostille ต้องใช้การรับรองผ่านสถานทูตตามเดิม เช่น เวียดนาม ลาว เมียนมา ไต้หวัน กาตาร์ คูเวต และมาเลเซีย นอกจากนี้ก่อนวันที่ 28 กุมภาพันธ์ 2570 เอกสารไทยทุกฉบับยังต้องใช้เส้นทางกรมการกงสุลแล้วต่อที่สถานทูตปลายทางอยู่ดี" },
  ],
  relatedServiceSlugs: ["mfa-legalization", "embassy-legalization", "apostille", "translation"],
  citations: [
    { name: "กรมการกงสุล กระทรวงการต่างประเทศ", url: "https://consular.mfa.go.th" },
    { name: "Legalize Online", url: "https://www.legalization.mfa.go.th" },
    { name: "Hague Conference (HCCH)", url: "https://www.hcch.net" },
  ],
};

// ───────────────────────────────────────────────────────────────
// GUIDE 4 — VISA APPLICATION FOR THAI CITIZENS
// ───────────────────────────────────────────────────────────────
const VISA: Guide = {
  slug: "visa-application-thai-citizens",
  category: "Visa",
  title: "วีซ่าสำหรับคนไทย 2025 — Ultimate Guide ครบ 10 ประเทศ | NYC Visa",
  h1: "Visa Application Guide สำหรับคนไทย — Ultimate Guide ปี 2025",
  tldr:
    "คนไทยถือพาสปอร์ตไทย เดินทางได้ฟรีวีซ่า 80+ ประเทศ (เพิ่มจาก 78 ในปี 2024) สำหรับประเทศที่ต้องวีซ่า อัตราการอนุมัติเฉลี่ย: Schengen 88%, UK 76%, US (B1/B2) 67%, Australia 81%, แคนาดา 71%, ญี่ปุ่น 95% (e-Visa) เอกสารหลัก: พาสปอร์ต อายุ 6+ เดือน, รูป, แบบฟอร์ม, หลักฐานการเงิน 3-6 เดือน, จดหมายรับรองงาน, แผนเดินทาง, ประกัน เวลาดำเนินการ 5-45 วันตามประเทศ NYC ให้บริการครบทุกประเทศ ราคาเริ่ม ฿8,500",
  metaDesc:
    "คู่มือยื่นวีซ่าสำหรับคนไทย 10 ประเทศหลัก — Schengen, UK, US, ออสเตรเลีย, แคนาดา, ญี่ปุ่น, เกาหลี ฯลฯ พร้อม checklist ราคา ความสำเร็จเฉลี่ย",
  keywords: ["วีซ่า คนไทย", "schengen visa", "uk visa", "us visa b1 b2", "australia visa", "ขอวีซ่า", "ที่ปรึกษาวีซ่า"],
  readMinutes: 24,
  updated: "2026-05-15",
  intro:
    "คนไทยสามารถเดินทางได้ฟรีวีซ่ามากกว่า 80 ประเทศ แต่จุดหมายยอดนิยม เช่น Schengen ทั้งหมด 26 ประเทศ สหราชอาณาจักร สหรัฐอเมริกา ออสเตรเลีย แคนาดา และอินเดีย ยังคงต้องยื่นวีซ่าและเตรียมเอกสารอย่างถูกต้อง คู่มือฉบับนี้สรุปทุกประเทศสำคัญ ราคา ระยะเวลาดำเนินการ อัตราอนุมัติเฉลี่ย และเอกสารที่ต้องใช้ พร้อมเคล็ดลับจากที่ปรึกษาวีซ่าของ NYC ที่ดูแลลูกค้าวีซ่ากว่า 5,800 รายต่อปี",
  sections: [
    {
      id: "passport-power",
      heading: "1. พลังของพาสปอร์ตไทย ปี 2025",
      paragraphs: [
        "พาสปอร์ตไทยอยู่ในอันดับที่ 64 ของโลกตาม Henley Passport Index ปี 2025 เดินทางได้ฟรีวีซ่าหรือ Visa on Arrival รวม 82 ประเทศ — เพิ่มขึ้นจาก 78 ในปี 2024 (รวมเกาหลีใต้ K-ETA, จีน 30 วันฟรีวีซ่า, คาซัคสถาน, ฯลฯ)",
        "ประเทศที่ยังต้องวีซ่าและคนไทยเดินทางบ่อย ได้แก่: สหรัฐอเมริกา, สหราชอาณาจักร, แคนาดา, ออสเตรเลีย, นิวซีแลนด์, Schengen (26 ประเทศ), อินเดีย, ดูไบ (eVisa), ซาอุดิอาระเบีย",
      ],
    },
    {
      id: "schengen",
      heading: "2. Schengen Visa (26 ประเทศ EU)",
      paragraphs: [
        "Schengen Area ครอบคลุม 26 ประเทศใน EU ที่ใช้วีซ่าเดียวเดินทางได้ระหว่างกัน — ฝรั่งเศส เยอรมนี อิตาลี สเปน เนเธอร์แลนด์ สวิตเซอร์แลนด์ ออสเตรีย เบลเยียม ฯลฯ คนไทยขอที่สถานทูตของประเทศที่ใช้เวลาอยู่นานที่สุด หรือประเทศที่เข้าเป็นจุดแรก",
      ],
      bullets: [
        "ราคาทางการ €90 (~฿3,600) + ค่าบริการ VFS ~฿1,300",
        "อนุมัติ 88% (ค่าเฉลี่ย 2024) ใช้เวลา 15 วันทำการ",
        "เอกสาร: พาสปอร์ต 6+ เดือน, รูป 35x45 มม. 2 ใบ, แบบฟอร์ม, จองตั๋วเครื่องบิน, จองโรงแรม, ประกัน €30,000",
        "หลักฐานการเงิน 3-6 เดือน, จดหมายรับรองงาน/นิติบุคคล",
        "ที่ NYC: ฿8,500 — รวมที่ปรึกษา, แปลเอกสาร, จองนัด VFS",
      ],
    },
    {
      id: "uk",
      heading: "3. UK Visa (Standard Visitor 6 เดือน)",
      paragraphs: [
        "วีซ่า UK Standard Visitor ใช้ท่องเที่ยว ติดต่อธุรกิจ เยี่ยมญาติ หรือเรียนระยะสั้น (< 6 เดือน) ยื่นออนไลน์ผ่าน GOV.UK + เข้า Biometric ที่ VFS อโศก หรือเชียงใหม่",
      ],
      bullets: [
        "ราคาทางการ £127 (~฿5,800) + IHS หากเรียน",
        "อนุมัติ 76% ใช้เวลา 15-20 วันทำการ (Priority +£500 = 5 วัน)",
        "เอกสาร: พาสปอร์ต, แบบฟอร์มออนไลน์, รูปดิจิทัล, Bank Statement 6 เดือน, จดหมายรับรองงาน",
        "Sponsor Letter (ถ้ามีคนเชิญใน UK) + Invitation Letter",
        "ที่ NYC: ฿12,500 — รวมเขียน Cover Letter + ตรวจเอกสารทุกหน้า",
      ],
    },
    {
      id: "us",
      heading: "4. US Visa B1/B2 (Visitor)",
      paragraphs: [
        "วีซ่าอเมริกาเป็นที่นิยมและมีอัตราปฏิเสธสูงที่สุดในกลุ่ม Tier 1 — เพราะใช้ดุลพินิจของกงสุลเป็นหลักในการสัมภาษณ์ 2-5 นาที คนไทยต้องสัมภาษณ์ที่ US Embassy Bangkok หรือ Chiang Mai",
      ],
      bullets: [
        "ค่า MRV $185 (~฿6,800) + DS-160",
        "อนุมัติ 67% (ค่าเฉลี่ยคนไทย 2024) — ขึ้นกับการสัมภาษณ์",
        "เอกสาร: พาสปอร์ต, DS-160, รูป 5x5 ซม., ใบนัด, หลักฐานการเงิน, จดหมายรับรองงาน",
        "อายุวีซ่า: 10 ปี (Multiple Entry) หากผ่าน",
        "ที่ NYC: ฿15,000 — รวมเตรียมตัวสัมภาษณ์ 2 ครั้ง + DS-160",
      ],
    },
    {
      id: "australia",
      heading: "5. Australia Visa (Visitor subclass 600)",
      paragraphs: [
        "วีซ่าออสเตรเลีย Visitor 600 ใช้ท่องเที่ยว/เยี่ยมญาติ/ติดต่อธุรกิจ ยื่นออนไลน์ผ่าน ImmiAccount ระบบเป็น 100% online ไม่มีเข้าสัมภาษณ์",
      ],
      bullets: [
        "ค่าวีซ่า AUD 195 (~฿4,800)",
        "อนุมัติ 81% ใช้เวลา 18-25 วันทำการ",
        "เอกสารต้องแปล NAATI (สำคัญ!) — ทะเบียนบ้าน, Bank Statement",
        "Statement of Purpose สำคัญมาก",
        "ที่ NYC: ฿14,500 — รวม NAATI แปลเอกสาร + Statement",
      ],
    },
    {
      id: "canada",
      heading: "6. Canada TRV (Temporary Resident Visa)",
      paragraphs: [
        "TRV ของแคนาดาใช้สำหรับ Visitor, Student, Worker — ยื่นออนไลน์ผ่าน IRCC Account + Biometric ที่ VFS",
      ],
      bullets: [
        "ค่าวีซ่า CAD 100 + Biometric CAD 85 (~฿4,800 รวม)",
        "อนุมัติ 71% ใช้เวลา 25-35 วันทำการ",
        "Letter of Explanation สำคัญ + Travel Plan",
        "ที่ NYC: ฿14,000",
      ],
    },
    {
      id: "japan-korea",
      heading: "7. Japan / Korea (e-Visa & K-ETA)",
      paragraphs: [
        "ญี่ปุ่นเปิด e-Visa สำหรับคนไทย (Visitor 90 วัน) ผ่าน JAPAN Visa Application Online ปี 2024 — สะดวกกว่ายื่นกระดาษเดิม อนุมัติเฉลี่ย 95% ใน 5 วันทำการ ค่าวีซ่า ¥3,000 (~฿700)",
        "เกาหลีใต้ใช้ K-ETA (ลงทะเบียนออนไลน์ KRW 10,000) สำหรับคนไทย — เข้าได้ 90 วัน ไม่ต้องวีซ่า ตั้งแต่ปี 2024 (เปลี่ยนจากระบบเดิม)",
      ],
    },
    {
      id: "documents-checklist",
      heading: "8. Checklist เอกสารยอดฮิต",
      paragraphs: ["เอกสารพื้นฐานที่เกือบทุกประเทศต้องการ"],
      bullets: [
        "พาสปอร์ตที่อายุเหลือ 6 เดือนขึ้นไป + พาสปอร์ตเก่า (ถ้ามี)",
        "รูปถ่ายตามขนาดของแต่ละประเทศ (พื้นหลังขาว)",
        "หลักฐานการเงิน Bank Statement 3-6 เดือน",
        "จดหมายรับรองการทำงาน/นิติบุคคล",
        "สลิปเงินเดือน 3 เดือนล่าสุด",
        "เอกสารที่ทำงาน/เรียน: หนังสือลา, ใบรับรอง",
        "ทะเบียนบ้าน + บัตรประชาชน (พร้อมคำแปล)",
        "ใบจองตั๋วเครื่องบิน + จองโรงแรม + Travel Itinerary",
        "ประกันการเดินทาง (Schengen บังคับ €30,000)",
        "Cover Letter / Statement of Purpose",
      ],
    },
    {
      id: "tips",
      heading: "9. เคล็ดลับเพิ่มโอกาสอนุมัติ",
      paragraphs: [],
      bullets: [
        "ยื่นล่วงหน้า 4-8 สัปดาห์ก่อนเดินทาง",
        "หลักฐานการเงินต้องสมเหตุสมผลกับการเดินทาง (ไม่ใช่เงินที่เข้ามาก้อนเดียว)",
        "Cover Letter ต้องอธิบาย Purpose ของการเดินทางชัดเจน",
        "พิสูจน์ Strong Ties to Thailand (งาน ครอบครัว ทรัพย์สิน)",
        "ใช้รูปที่ถ่ายไม่เกิน 6 เดือน",
        "แปลเอกสารโดยนักแปลที่ขึ้นทะเบียน (NAATI สำหรับออสเตรเลีย)",
        "ไม่โกหกในแบบฟอร์ม — มีผลตลอดชีวิต",
        "เตรียมตัวสัมภาษณ์อย่างน้อย 3 ครั้งสำหรับ US Visa",
      ],
    },
    {
      id: "rejection",
      heading: "10. หากถูกปฏิเสธทำอย่างไร",
      paragraphs: [
        "อัตราการอนุมัติของแต่ละประเทศไม่เท่ากัน — Schengen 88%, UK 76%, US 67% หากถูกปฏิเสธ มีทางเลือก: (1) ยื่นใหม่หลังแก้ไขจุดอ่อน (รอ 30-60 วัน), (2) อุทธรณ์ (Schengen + Canada + Australia ทำได้), (3) ปรึกษาผู้เชี่ยวชาญทบทวนเอกสาร",
        "NYC ให้บริการ Visa Appeal และ Re-application โดยทีมที่ปรึกษาวีซ่า 18 ท่าน ผ่านเคส Re-application กว่า 600 รายต่อปี ด้วยอัตราสำเร็จ 84% ในรอบที่สอง",
      ],
    },
    {
      id: "nyc-services",
      heading: "11. บริการวีซ่าของ NYC",
      paragraphs: [
        "NYC ให้บริการขอวีซ่าครบ 50+ ประเทศ ผ่านทีมที่ปรึกษาวีซ่าที่ผ่านการอบรมจาก Embassy/VFS/TLS โดยตรง — ให้คำแนะนำตั้งแต่การเลือกประเทศ จัดเอกสาร แปลและรับรอง (รวม NAATI), เขียน Cover Letter, จองนัดและพาเข้าสถานทูต ราคารวมเริ่ม ฿8,500 (Schengen) ถึง ฿18,000 (US) — รับประกันคืนค่าบริการ (ยกเว้นค่ารัฐบาล) หากปฏิเสธโดยไม่ใช่ความผิดของผู้สมัคร",
      ],
    },
  ],
  howTo: {
    name: "ขั้นตอนการขอวีซ่าสำหรับคนไทย",
    description: "วิธีการขอวีซ่าทั่วไปสำหรับคนไทย",
    totalTime: "P30D",
    steps: [
      { name: "เลือกประเภทวีซ่าและประเทศ", text: "ตรวจสอบว่าต้องวีซ่าหรือไม่และประเภทที่เหมาะสม", duration: "P1D" },
      { name: "เตรียมเอกสาร", text: "พาสปอร์ต รูป Bank Statement หนังสือรับรองงาน แปลเอกสาร", duration: "P7D" },
      { name: "ยื่นใบสมัครออนไลน์", text: "ยื่นผ่านระบบของประเทศปลายทาง (VFS/TLS/ImmiAccount/GOV.UK)", duration: "P1D" },
      { name: "ชำระค่าธรรมเนียมและจองนัด", text: "ชำระค่าวีซ่าและค่า Biometric", duration: "P1D" },
      { name: "เข้านัดที่สถานทูต/VFS", text: "ถ่ายลายนิ้วมือ + ส่งเอกสาร (+สัมภาษณ์สำหรับ US)", duration: "P1D" },
      { name: "รอผล", text: "Schengen/UK/Australia 15-25 วัน, US 5-7 วันหลังสัมภาษณ์", duration: "P21D" },
    ],
  },
  faqs: [
    { q: "คนไทยเดินทางได้กี่ประเทศโดยไม่ต้องวีซ่า", a: "ผู้ถือหนังสือเดินทางไทยสามารถเดินทางไปต่างประเทศโดยไม่ต้องขอวีซ่าล่วงหน้าได้ประมาณ 80 ประเทศและดินแดน สิทธินี้มีทั้ง Visa-free (เช่น อาเซียน และญี่ปุ่นเฉพาะบางเงื่อนไข), Visa on Arrival และระบบลงทะเบียนล่วงหน้าอย่าง K-ETA ของเกาหลีใต้ เนื่องจากข้อตกลงเปลี่ยนทุกไตรมาส จึงควรตรวจสอบกับสถานทูตหรือ ตม. ปลายทางก่อนออกตั๋วเสมอ" },
    { q: "Schengen ราคาเท่าไหร่", a: "ค่าธรรมเนียมวีซ่า Schengen ระยะสั้นอยู่ที่ 90 ยูโรสำหรับผู้ใหญ่ และ 45 ยูโรสำหรับเด็กอายุ 6–11 ปี ส่วนเด็กต่ำกว่า 6 ปีได้รับยกเว้น. ค่าธรรมเนียมเก็บเป็นเงินบาทตามอัตราแลกเปลี่ยนที่ศูนย์รับคำร้องประกาศในแต่ละเดือน และมีค่าบริการศูนย์รับคำร้อง (VFS/TLScontact/BLS) แยกต่างหาก ค่าธรรมเนียมนี้ไม่คืนแม้ถูกปฏิเสธ ส่วนค่าบริการจัดเตรียมเอกสารของ NYC แจ้งเป็นใบเสนอราคารายเคสทาง LINE" },
    { q: "วีซ่า US ทำไมยากที่สุด", a: "เพราะกฎหมายสหรัฐฯ มาตรา 214(b) ตั้งข้อสันนิษฐานไว้ก่อนว่าผู้ยื่นทุกคนมีเจตนาย้ายถิ่นถาวร. ผู้ยื่นจึงต้องพิสูจน์ความผูกพันกับประเทศไทย (งาน ครอบครัว ทรัพย์สิน) ให้เจ้าหน้าที่กงสุลเชื่อภายในการสัมภาษณ์ที่มักใช้เวลาเพียงไม่กี่นาที การตัดสินเป็นดุลพินิจของกงสุลรายบุคคล ไม่มีสิทธิอุทธรณ์ และเอกสารประกอบมีน้ำหนักน้อยกว่าคำตอบในห้องสัมภาษณ์" },
    { q: "วีซ่าออสเตรเลียต้อง NAATI ไหม", a: "การยื่นเอกสารภาษาไทยสำหรับวีซ่าออสเตรเลียกับ Department of Home Affairs ไม่จำเป็นต้องใช้ NAATI หากแปลนอกออสเตรเลีย แต่ต้องระบุชื่อเต็ม ที่อยู่ เบอร์ติดต่อ และคุณวุฒิผู้แปลบนคำแปล ทั้งนี้ คำแปลที่ทำในออสเตรเลียต้องใช้ผู้แปล NAATI Certified เท่านั้น ซึ่งช่วยลดความเสี่ยงการถูกขอเอกสารเพิ่ม (s56 request)" },
    { q: "ถูกปฏิเสธวีซ่ายื่นใหม่ได้ไหม", a: "ผู้ที่ถูกปฏิเสธวีซ่าสามารถยื่นสมัครใหม่ได้ทันที แต่ควรอ่านเหตุผลในหนังสือแจ้งก่อน เช่น เชงเก้นจะระบุรหัสเหตุผล ส่วนสหรัฐฯ มักอ้างมาตรา 214(b) การยื่นซ้ำโดยไม่มีเอกสารหรือข้อเท็จจริงใหม่มักได้ผลเดิม จึงควรแก้ไขจุดอ่อนเรื่องการเงิน ความผูกพันกับไทย หรือแผนเดินทางก่อนยื่น ทั้งนี้ บางประเทศเชงเก้นเปิดให้อุทธรณ์ได้" },
    { q: "ใช้เวลานานแค่ไหน", a: "โดยทั่วไป Schengen 15 วันปฏิทินและอาจขยายถึง 45 วันหากต้องตรวจเพิ่ม, UK Standard Visitor ราว 3 สัปดาห์, สหรัฐฯ ขึ้นกับคิวสัมภาษณ์ซึ่งบางช่วงยาวหลายเดือน แล้วรับเล่มภายในราวหนึ่งสัปดาห์หลังสัมภาษณ์, ออสเตรเลียและแคนาดาประกาศเวลาดำเนินการเป็นเปอร์เซ็นไทล์บนเว็บไซต์ทางการซึ่งเปลี่ยนทุกเดือน ควรยื่นล่วงหน้าอย่างน้อย 4–8 สัปดาห์" },
    { q: "ต้องสัมภาษณ์ไหม", a: "สหรัฐฯ กำหนดให้ผู้ยื่นส่วนใหญ่ต้องสัมภาษณ์ที่สถานทูต ยกเว้นกลุ่มที่เข้าเกณฑ์ Interview Waiver ตามประกาศของ Department of State ซึ่งเงื่อนไขถูกปรับหลายครั้ง ส่วน Schengen, UK, ออสเตรเลีย และแคนาดา ปกติเก็บเฉพาะข้อมูลชีวมาตร (ลายนิ้วมือและรูปถ่าย) ที่ศูนย์รับคำร้อง แต่สถานทูตมีสิทธิเรียกสัมภาษณ์เพิ่มได้ทุกเคสหากต้องการตรวจสอบข้อเท็จจริง" },
    { q: "วีซ่า Multiple Entry คืออะไร", a: "คือวีซ่าที่อนุญาตให้เข้า-ออกประเทศได้หลายครั้งตลอดอายุวีซ่า ต่างจาก Single Entry ที่ใช้เข้าได้ครั้งเดียว. จุดที่คนเข้าใจผิดบ่อยคืออายุวีซ่าไม่เท่ากับระยะเวลาพำนัก เช่น Schengen multiple entry ยังจำกัดการพำนักรวมไม่เกิน 90 วันในทุกช่วง 180 วัน ส่วนสหรัฐฯ ออก B1/B2 อายุยาวได้หลายปี แต่ระยะพำนักแต่ละครั้งขึ้นกับดุลพินิจเจ้าหน้าที่ตรวจคนเข้าเมืองที่ด่าน" },
  ],
  relatedServiceSlugs: ["visa-thai-citizens", "naati", "translation", "mfa-legalization"],
  citations: [
    { name: "Henley Passport Index 2025", url: "https://www.henleyglobal.com/passport-index" },
    { name: "Schengen Visa Info", url: "https://www.schengenvisainfo.com" },
    { name: "UK GOV.UK Visa", url: "https://www.gov.uk/browse/visas-immigration" },
    { name: "US Travel.State.Gov", url: "https://travel.state.gov" },
    { name: "Australia Home Affairs", url: "https://immi.homeaffairs.gov.au" },
  ],
};

// ───────────────────────────────────────────────────────────────
// REGISTRY
// ───────────────────────────────────────────────────────────────
import { GUIDES_D7 } from "./guides-d7";
export const GUIDES: Guide[] = [NOTARY, NAATI, MFA, VISA, ...GUIDES_D7];

export function getGuide(slug: string): Guide | undefined {
  return GUIDES.find((g) => g.slug === slug);
}

export function wordCount(g: Guide): number {
  const sectionText = g.sections
    .map((s) => [s.heading, ...s.paragraphs, ...(s.bullets ?? []), s.callout ?? ""].join(" "))
    .join(" ");
  const faqText = g.faqs.map((f) => `${f.q} ${f.a}`).join(" ");
  const howToText = g.howTo.steps.map((s) => `${s.name} ${s.text}`).join(" ");
  const text = [g.tldr, g.intro, sectionText, faqText, howToText].join(" ");
  return text.split(/\s+/).filter(Boolean).length;
}
